import { useState } from "react"
import { Box, Button, Card, IconButton, Popover, TextField, Typography } from "@mui/material"
import TipTapEditor from "@crm-src/components/tiptap-editor"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { Trash2, Palette, Type } from "lucide-react"
// Sanitize untrusted rich-text HTML (DOMPurify-backed) before injecting
// into the visual preview. Matches the protection main applies inline.
import { sanitizeHtmlWithStyles } from "@crm-src/lib/sanitize"
import { NewsletterSection } from "./stock-images"

interface CaseStudySectionProps {
  section: NewsletterSection
  isEditing: boolean
  onUpdate: (sectionId: string, partial: any) => void
  onChangeColor: (sectionId: string, color: string) => void
  onDelete: (sectionId: string) => void
  setEditingSection: (id: string | null) => void
}

export function CaseStudySection({
  section,
  isEditing,
  onUpdate,
  onChangeColor,
  onDelete,
  setEditingSection,
}: CaseStudySectionProps) {
  const [colorAnchor, setColorAnchor] = useState<null | HTMLElement>(null)
  return (
    <Box
      className="group"
      sx={{ position: "relative" }}
      style={{ backgroundColor: section.backgroundColor || '#ffffff', padding: '17px 40px' }}
    >
      {/* Section controls */}
      <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1, bgcolor: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)", borderRadius: 1, boxShadow: 1, p: 0.5 }}>
        <IconButton size="small" onClick={(e) => setColorAnchor(e.currentTarget)}>
          <Palette size={16} />
        </IconButton>
        <Popover
          open={Boolean(colorAnchor)}
          anchorEl={colorAnchor}
          onClose={() => setColorAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { width: 288, p: 2 } } }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography component="label" sx={{ fontSize: "1rem", fontWeight: 600 }}>Background Color</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
              {[
                { color: '#ffffff', label: 'White' },
                { color: '#f9fdf7', label: 'Light Green' },
                { color: '#fef8e7', label: 'Light Beige' },
                { color: '#fff9e6', label: 'Cream' },
              ].map(({ color, label }) => (
                <Box
                  component="button"
                  key={color}
                  onClick={() => onChangeColor(section.id, color)}
                  sx={{ position: "relative", height: 64, borderRadius: 1, border: "2px solid", transition: "all 0.15s", cursor: "pointer", "&:hover": { transform: "scale(1.05)", boxShadow: 3 } }}
                  style={{ backgroundColor: color, borderColor: section.backgroundColor === color ? '#6B8E23' : '#d1d5db' }}
                >
                  <Box component="span" sx={{ position: "absolute", bottom: 4, left: 8, fontSize: 12, fontWeight: 500, color: "text.secondary" }}>{label}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Popover>
        <IconButton size="small" onClick={() => setEditingSection(section.id)}>
          <Type size={16} />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => onDelete(section.id)}>
          <Trash2 size={16} />
        </IconButton>
      </Box>

      {/* Case Study Content */}
      {isEditing ? (
        <Card sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 700, mb: 2 }}>Edit Case Study</Typography>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Section Heading</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="case study section heading"
                  placeholder="e.g., success story, client spotlight"
                  currentValue={section.content.heading}
                  onGenerate={(content) => onUpdate(section.id, { heading: content })}
                />
                <AiTextImprover
                  fieldType="short"
                  context="case study section heading"
                  currentText={section.content.heading}
                  onImprove={(content) => onUpdate(section.id, { heading: content })}
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              value={section.content.heading}
              onChange={(e) => onUpdate(section.id, { heading: e.target.value })}
              placeholder="e.g., Success Story"
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Title</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="case study title - a compelling headline about customer success"
                  placeholder="e.g., aviation company improved emergency communications"
                  currentValue={section.content.title}
                  onGenerate={(content) => onUpdate(section.id, { title: content })}
                />
                <AiTextImprover
                  fieldType="short"
                  context="case study title"
                  currentText={section.content.title}
                  onImprove={(content) => onUpdate(section.id, { title: content })}
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              value={section.content.title}
              onChange={(e) => onUpdate(section.id, { title: e.target.value })}
              placeholder="e.g., How We Helped Transform Their Business"
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Content</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="long"
                  context="case study content - detailed story about customer success and results"
                  placeholder="e.g., describe the customer's challenge and how your solution helped"
                  currentValue={section.content.content}
                  onGenerate={(content) => onUpdate(section.id, { content })}
                />
                <AiTextImprover
                  fieldType="long"
                  context="case study content"
                  currentText={section.content.content?.replace(/<[^>]*>/g, '') || ''}
                  onImprove={(content) => onUpdate(section.id, { content: `<p>${content}</p>` })}
                />
              </Box>
            </Box>
            <TipTapEditor
              value={section.content.content}
              onChange={(html) => onUpdate(section.id, { content: html })}
            />
          </Box>

          <Button color="success" variant="contained" onClick={() => setEditingSection(null)}>Save</Button>
        </Card>
      ) : (
        <div>
          {/* Header — render only when set. (No "Success Story" placeholder:
              it confused the preview, and a greeting/intro section is meant to
              have no heading at all.) */}
          {section.content.heading ? (
            <h2 style={{ color: '#8B4513', fontSize: '24px', marginBottom: '20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif', borderBottom: '2px solid #f0e68c', paddingBottom: '10px' }}>
              {section.content.heading}
            </h2>
          ) : null}

          {/* Title — render only when set */}
          {section.content.title ? (
            <h3 style={{ color: '#2d3748', fontSize: '20px', marginBottom: '15px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif' }}>
              {section.content.title}
            </h3>
          ) : null}

          {/* Content — DOMPurify-sanitized rich text from TipTap editor */}
          <div
            style={{ color: '#2d3748', fontSize: '14px', lineHeight: '1.6' }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtmlWithStyles(section.content.content) }}
          />
        </div>
      )}
    </Box>
  )
}

export function caseStudyToHtml(section: NewsletterSection): string {
  const headingHtml = section.content.heading
    ? `<h2 style="color: #8B4513; font-size: 24px; margin: 0 0 20px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; border-bottom: 2px solid #f0e68c; padding-bottom: 10px;">${section.content.heading}</h2>`
    : ''
  const titleHtml = section.content.title
    ? `<h3 style="color: #2d3748; font-size: 20px; margin: 0 0 15px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;">${section.content.title}</h3>`
    : ''
  return `
          <!-- Case Study Section -->
          <tr>
            <td style="background-color: ${section.backgroundColor || '#ffffff'}; padding: 17px 40px;">
              ${headingHtml}
              ${titleHtml}
              <div style="color: #2d3748; font-size: 14px; line-height: 1.6;">
                ${section.content.content}
              </div>
            </td>
          </tr>
`
}
